import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Forward, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtShort, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Pill, Spinner } from "@/components/common";
import { RedirectDialog } from "@/components/delivery/RedirectDialog";

const stateOf = (f) => {
  if (f.redirected_to?.length) return { label: "resent", tone: "blue" };
  if (["bounced", "complained", "failed"].includes(f.delivery_status) && f.status === "sent") return { label: f.delivery_status === "complained" ? "spam" : f.delivery_status === "bounced" ? "bounced" : "rejected", tone: "red" };
  if (!f.final) return { label: "retrying", tone: "amber" };
  return { label: "failed", tone: "red" };
};

function FailureRow({ f, onChanged }) {
  const [redirect, setRedirect] = useState(false);
  const st = stateOf(f);
  const retry = useMutation({ mutationFn: () => api.post(`/delivery/${f.channel}/${f.id}/retry`).then((r) => r.data), onSuccess: (d) => { d.status === "sent" ? toast.success(`${f.channel === "email" ? "Email" : "Slack message"} sent`) : toast.error(`Still failing: ${d.error}`); onChanged(); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <li className="py-2 px-1 text-xs grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1 items-start row-hover rounded" data-testid={`delivery-failure-${f.id}`}>
      <span className="font-mono text-slate-500 whitespace-nowrap pt-0.5">{fmtShort(f.sent_at)}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <Pill tone={st.tone} testId={`delivery-failure-state-${f.id}`}>{st.label}</Pill>
          <Pill tone={f.channel === "email" ? "blue" : "violet"}>{f.channel}</Pill>
          <span className="text-slate-200 font-semibold">{f.kind_label}</span>
          <span className="text-slate-400">→ {f.recipient}</span>
          {f.sandbox && <Pill tone="amber">sandbox</Pill>}
        </div>
        <div className="text-slate-500 truncate mt-0.5" title={f.subject}>{f.subject}</div>
        <div className="text-red-300/90 mt-0.5 break-words" data-testid={`delivery-failure-error-${f.id}`}>{f.error}{f.attempts > 1 ? ` · ${f.attempts} attempts` : ""}{!f.final && f.next_retry_at ? ` · next retry ${relTime(f.next_retry_at)}` : ""}{f.redirected_to?.length ? ` · resent to ${f.redirected_to.join(", ")}` : ""}</div>
      </div>
      <div className="flex gap-1 col-span-2 sm:col-span-1 justify-end">
        <Button data-testid={`delivery-retry-${f.id}`} size="sm" variant="outline" className="h-7 text-[11px] border-[#2d3d58] bg-[#0f172a]" disabled={retry.isPending || !!f.redirected_to?.length} onClick={() => retry.mutate()}>{retry.isPending ? <Spinner /> : <RefreshCw className="h-3 w-3 mr-1" />} Retry</Button>
        {f.channel === "email" && <Button data-testid={`delivery-redirect-${f.id}`} size="sm" variant="outline" className="h-7 text-[11px] border-[#2d3d58] bg-[#0f172a]" onClick={() => setRedirect(true)}><Forward className="h-3 w-3 mr-1" /> Resend to…</Button>}
      </div>
      {redirect && <RedirectDialog failure={f} onClose={() => setRedirect(false)} onDone={onChanged} />}
    </li>
  );
}

export function DeliveryFailures({ failures, onChanged }) {
  if (!failures.length) return <div className="text-xs text-emerald-300/80 flex items-center gap-2 panel p-3 bg-[#0f172a]" data-testid="delivery-no-failures"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" /> No failed, bounced or spam-flagged messages in this range.</div>;
  return (
    <div>
      <div className="eyebrow mb-1.5 text-red-300 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" /> Needs attention · {failures.length}</div>
      <ul className="divide-y divide-[#1f293d] max-h-80 overflow-y-auto" data-testid="delivery-failures-list">{failures.map((f) => <FailureRow key={`${f.channel}-${f.id}`} f={f} onChanged={onChanged} />)}</ul>
    </div>
  );
}
